import i18nextHTTPBackend from 'i18next-http-backend';
import i18nextIntervalPluralPostProcessor from 'i18next-intervalplural-postprocessor';
import type { ChainedBackendOptions } from 'i18next-chained-backend';
import i18ChainedBackend from 'i18next-chained-backend';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { InitOptions as I18NInitOptions } from 'i18next';

import { logger } from '@luna/logger';

import type { TFunction } from './types';
import type { Language, LanguageVariant, LanguageVariantWithCiMode } from './Language';
import { overrideSuffixSingleton } from './overrideSuffixService';
import { setReactDomServer } from './reactDomServerSingleton';

type FormatFuncFH = (value: any, pattern: string | undefined) => any;

type CustomHeaders = { [key: string]: string } | (() => { [key: string]: string });

type InitOptions = {
    currentLanguage: LanguageVariantWithCiMode;
    fallbackLanguage: Language;
    availableLanguages: LanguageVariant[];
    localesBasePath: string;
    overrideSuffix?: string;
    interpolationOption?: FormatFuncFH;
    namespaces?: string[];
    resources?: I18NInitOptions['resources'];
    customHeaders?: CustomHeaders;
    appendNamespaceToCIMode?: boolean;
    /**
     * Defaults to true - must be disabled in non-web environments
     */
    initReactDomServer?: boolean;
    additionalLocaleLocations?: string[];
    queryStringParams?: Record<string, unknown>;
    defaultNamespace?: string;
};

const DEFAULT_NAMESPACE = 'translation';

function ensurePublicUrlEndsWithSlash(): string {
    const publicUrl = process.env.PUBLIC_URL || '';
    if (!publicUrl.endsWith('/')) {
        return `${publicUrl}/`;
    }
    return publicUrl;
}

export async function initI18n({
    currentLanguage,
    fallbackLanguage,
    availableLanguages,
    localesBasePath,
    overrideSuffix,
    interpolationOption,
    namespaces = [],
    customHeaders,
    resources,
    appendNamespaceToCIMode = true,
    initReactDomServer = true,
    additionalLocaleLocations = [],
    queryStringParams,
    defaultNamespace = DEFAULT_NAMESPACE,
}: InitOptions): Promise<TFunction> {
    overrideSuffixSingleton.set(overrideSuffix);

    if (initReactDomServer) {
        try {
            setReactDomServer(await import('react-dom/server'));
        } catch (e) {
            logger.error('Loading react-dom/server failed', e);
        }
    }

    return i18next
        .use(i18ChainedBackend)
        .use(i18nextIntervalPluralPostProcessor)
        .use(initReactI18next)
        .init({
            ns: namespaces.includes(defaultNamespace)
                ? [...namespaces]
                : [...namespaces, defaultNamespace],
            defaultNS: getDefaultNamespace(namespaces),
            fallbackNS: defaultNamespace,
            lng: currentLanguage,
            compatibilityJSON: 'v3',
            fallbackLng: fallbackLanguage,
            supportedLngs: availableLanguages,
            resources,
            interpolation: { escapeValue: false, skipOnVariables: false, ...interpolationOption },
            appendNamespaceToCIMode,
            returnObjects: false, // if false -> displays message if translation is an unexpected object instead of string
            backend: {
                backends: [
                    i18nextHTTPBackend,
                    i18nextHTTPBackend,
                    ...additionalLocaleLocations.map(() => i18nextHTTPBackend),
                ],
                backendOptions: [
                    {
                        loadPath: `${localesBasePath}{{lng}}/{{ns}}.{{lng}}.json`,
                        customHeaders,
                        queryStringParams,
                    },
                    {
                        loadPath: `${ensurePublicUrlEndsWithSlash()}locales/{{ns}}.{{lng}}.json`, // local fallback
                        queryStringParams,
                    },
                    ...additionalLocaleLocations.map((location) => ({
                        loadPath: location,
                        queryStringParams,
                    })),
                ],
            } as ChainedBackendOptions,
        });
}

function getDefaultNamespace(namespaces: string[] = []): string {
    return namespaces[0] ?? DEFAULT_NAMESPACE;
}
